<template>
  <form @submit.prevent="submit">
    <div class="row">
      <div class="medium-12 columns">
        <label :class="{ error: $v.webhook.name.$error }">
          {{ $t('WEBHOOKS_SEND_MESSAGES.FORM.NAME.LABEL') }}

          <input v-model.trim="webhook.name" type="text" minlength="3" maxlength="255"
            :placeholder="$t('WEBHOOKS_SEND_MESSAGES.FORM.NAME.PLACEHOLDER')" @blur="$v.webhook.name.$touch" />

          <span v-if="$v.webhook.name.$error" class="message">
            {{ $t('WEBHOOKS_SEND_MESSAGES.FORM.NAME.ERROR') }}
          </span>
        </label>
      </div>
    </div>
    <div class="row">
      <div class="medium-12 columns">
        <label :class="{ error: $v.units.$error }">
          {{ $t('WEBHOOKS_SEND_MESSAGES.FORM.UNITS.LABEL') }}

          <input v-model.trim="units" type="text" minlength="3" maxlength="255"
            :placeholder="$t('WEBHOOKS_SEND_MESSAGES.FORM.UNITS.PLACEHOLDER')" @blur="$v.units.$touch" />

          <span v-if="$v.units.$error" class="message">
            {{ $t('WEBHOOKS_SEND_MESSAGES.FORM.UNITS.ERROR') }}
          </span>
        </label>
      </div>
    </div>

    <div class="row">
      <div class="medium-12 columns">
        <label>
          {{ $t('WEBHOOKS_SEND_MESSAGES.FORM.USERS.LABEL') }}

          <input v-model.trim="users" type="text" minlength="3" maxlength="255"
            :placeholder="$t('WEBHOOKS_SEND_MESSAGES.FORM.USERS.PLACEHOLDER')"/>
          
        </label>
      </div>
    </div>

    <div class="row">
      <div class="medium-12 columns">
        <label :class="{ error: $v.webhook.type_rules.$error }">
          {{ $t('WEBHOOKS_SEND_MESSAGES.FORM.TYPE_RULES.LABEL') }}
        </label>
        <multiselect
          ref="tagInput"
          v-model="webhook.type_rules"
          :placeholder="$t('WEBHOOKS_SEND_MESSAGES.FORM.TYPE_RULES.PLACEHOLDER')"
          :options="type_rules"
          :multiple="true"
          :taggable="true"
          :close-on-select="false"
          @tag="addTagValue"
        />
        <span v-if="$v.webhook.type_rules.$error" class="message">
          {{ $t('WEBHOOKS_SEND_MESSAGES.FORM.TYPE_RULES.ERROR') }}
        </span>
        
      </div>
    </div>

    <div class="row">
      <div class="medium-12 columns">
        <label :class="{ error: $v.webhook.consumer_phonenumber.$error }">
          {{ $t('WEBHOOKS_SEND_MESSAGES.FORM.CONSUMER_PHONENUMBER.LABEL') }}

          <input v-model.trim="webhook.consumer_phonenumber" type="text" minlength="3" maxlength="255"
            :placeholder="$t('WEBHOOKS_SEND_MESSAGES.FORM.CONSUMER_PHONENUMBER.PLACEHOLDER')" @blur="$v.webhook.consumer_phonenumber.$touch" />

          <span v-if="$v.webhook.consumer_phonenumber.$error" class="message">
            {{ $t('WEBHOOKS_SEND_MESSAGES.FORM.CONSUMER_PHONENUMBER.ERROR') }}
          </span>
        </label>
      </div>
    </div>

    <div class="row">
      <div v-for="(action, key) in webhook.actions" :key="action.key" class="medium-12 columns filters-wrap">
        <p><strong>Mensagem</strong> <span v-if="webhook.actions.length > 1"> - {{ key + 1 }}</span></p>
        <multiselect
            ref="`inboxInput${key}`"
            v-model="webhook.actions[key].inbox"
            track-by="id"
            label="name"
            :placeholder="$t('WEBHOOKS_SEND_MESSAGES.FORM.INBOX.PLACEHOLDER')"
            :options="inboxes"
          />

        <templates-picker
            v-if="!webhook.actions[key].template"
          :inbox-id="getInboxIdFromAction(webhook.actions[key])"
          :with-attachments="false"
          @onSelect="onSelectTemplate($event, key)"
        />

        <template-parser
          ref="templateParsers"
          v-if="webhook.actions[key].template"
          type="webhook_send_message"
          :template="webhook.actions[key].template"
          :inbox-id="getInboxIdFromAction(webhook.actions[key])"
          @onBlur="getEventTemplate($event, key)"
          @initTemplate="getEventTemplate($event, key)"
          :valueProcessedParams="(webhook.actions[key].template.initial_processed_params ? webhook.actions[key].template.initial_processed_params : {})"
        />

        <div class="webhook-actions">
          <div class="flex-end">
            <woot-button
              v-if="webhook.actions[key].template"
              @click.prevent="webhook.actions[key].template = null"
              icon="edit"
              color-scheme="danger"
              variant="smooth"
              size="small">
                {{ $t('WEBHOOKS_SEND_MESSAGES.FORM.CHANGE_TEMPLATE_BUTTON_TEXT') }}
            </woot-button>

            <woot-button
              v-if="webhook.actions.length > 1"
              @click.prevent="webhook.actions.splice(key, 1)"
              icon="delete"
              color-scheme="secondary"
              variant="smooth"
              size="small">
                {{ $t('WEBHOOKS_SEND_MESSAGES.FORM.REMOVE_MESSAGE_BUTTON_TEXT') }}
            </woot-button>
          </div>
        </div>
      </div>
    </div>
    <div class="flex-end">
      <woot-button type="submit" class="button primary"
        :disabled="$v.$invalid || isSubmitting"
          :is-loading="isSubmitting"
        >
        {{ $t('WEBHOOKS_SEND_MESSAGES.FORM.SUBMIT') }}
      </woot-button>
    </div>
  </form>  
</template>

<script>

import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker';
import TemplateParser from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplateParser';

import alertMixin from 'shared/mixins/alertMixin';

export default {
  mixins: [alertMixin],
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    TemplatesPicker,
    TemplateParser,
    MultiselectDropdown,
    WootMessageEditor,
  },
  data() {
    return {
      units: this.value.units ? this.value.units.join(', ') : '',
      users: this.value.users ? this.value.users.join(', ') : '',
      type_rules: [
        "Avaliação WEB C2B",
        "Collaboration Pós vendas",
        "Oficina",
        "Prospecção Oficina",
        "Somente compra",
        "Troca por veículo novo",
        "Troca por veículo seminovo",
        "Venda Direta",
        "Venda Frotista",
        "Venda PCD"
      ],
      webhook: {
        name: '',
        units: [],
        users: [],
        type_rules: [],
        consumer_phonenumber: '{{whatsappclient}}',
        actions: []
      },
      selectedInbox: null,
    };
  },
  validations() {
    return {
      units: { required },
      webhook: {
        name: { required, minLength: minLength(3), maxLength: maxLength(255) },
        units: { required },
        type_rules: { required },
        consumer_phonenumber: { required },        
        actions: {
          required, 
          $each: {
            inbox: { required },
            template: {
              required,
            },
          },
        },
      },
    };
  },
  watch: {
    units(newUnits) {
      this.webhook.units = newUnits.split(',').map(unit => unit.trim());
    },
    users(newUsers) {
      this.webhook.users = newUsers.toString().trim() != "" ? newUsers.toString().trim().split(',').map(user => user.trim()) : [];
    },
  },
  computed: {    
    inboxes() {
      return this.$store.getters['inboxes/getWhatsAppInboxes'];
    },
  },
  methods: {    
    async submit() {
      this.$v.$touch();

      if (this.$v.$invalid) return;

      let hasError = false;
      this.$refs.templateParsers.forEach(templateParser => {
        if (templateParser.$v) {
          templateParser.$v.$touch();
          if (templateParser.$v.$invalid) {
            hasError = true;
          }
        }
      });

      if (hasError) return;     

      this.$emit('submit', {
        name: this.webhook.name,
        units: [...new Set(this.webhook.units)],
        users: this.webhook.users.length > 0 ? [...new Set(this.webhook.users)] : null,
        type_rules: this.webhook.type_rules,
        consumer_phonenumber: this.webhook.consumer_phonenumber,
        url: this.webhook.url,
        actions: this.webhook.actions.map((action) => {
          return {
            inbox_id: action.inbox.id,
            message: action.message,
            parameters: action.parameters,
            template_params: action.templateParams,
            has_parameters: action.hasParameters,
          };
        })
      });
    },    
    addAction(event) {
      event && event.preventDefault();
      this.webhook.actions.push({
        key: Date.now() + Math.random().toString(36).substring(2, 11),
        inbox: null,
        template: null,
        parameters: [],
      });
    },
    onSelectTemplate(template, index) {
      template = JSON.parse(JSON.stringify(template));      
      delete template.initial_processed_params;
      this.webhook.actions[index].template = template;
    },
    resetTemplate(index) {
      this.webhook.actions[index].template = null;
    },
    getEventTemplate(event, index) {
      if (event.files) {
        this.webhook.actions[index].file = event.attachedFile;
      }

      if (!event.files) {
        const { message, templateParams, hasParameters, parameters } = event;
        
        this.webhook.actions[index].message = message;
        this.webhook.actions[index].templateParams = templateParams;
        this.webhook.actions[index].hasParameters = hasParameters ? hasParameters : false;
        this.webhook.actions[index].parameters = parameters ? Object.values(parameters) : [];
      }
    },
    generateName() {
      return `${this.$t('WEBHOOKS_SEND_MESSAGES.FORM.NAME.WEBHOOK')} - ${new Date().getTime()}`;
    },
    buildMessage(action) {
      let message = action.template.components[0].text;
      action.parameters.forEach((param, index) => {
        message = message.replace(`{{${index + 1}}}`, param);
      });
      return message;
    },
    addTagValue(tagValue) {
      const tags = tagValue
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag && !this.type_rules.includes(tag));

      this.type_rules.push(...[...new Set(tags)]);
      this.webhook.type_rules.push(...[...new Set(tags)]);
    },
    getInboxIdFromAction(action){
      if(action.inbox) {
        return action.inbox.id;
      }else if(action.inbox_id) {
        return action.inbox_id;
      }
      return null;
    }
  },
  mounted() {
    if(!this.value.id){
      this.webhook.name = this.generateName();
      this.addAction();
    }else{
      const value = JSON.parse(JSON.stringify(this.value));      
      this.webhook.name = value.name;
      this.webhook.units = value.units || [];
      this.webhook.users = value.users || [];
      this.webhook.type_rules = value.type_rules || [];
      this.webhook.consumer_phonenumber = value.consumer_phonenumber;
      this.webhook.actions = value.actions.map((action) => {
        action.inbox = this.$store.getters['inboxes/getWhatsAppInboxes'].find(inbox => inbox.id === action.inbox_id) || null;
        let template = action.inbox ? action.inbox.message_templates.find(template => template.name === action.template_params.name) : null;
        if(template && action.template_params?.processed_params){
          template.initial_processed_params = action.template_params.processed_params;
        }
        action.template = template;
        return action
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.filters-wrap {
  padding: var(--space-normal);
  border-radius: var(--border-radius-large);
  border: 1px solid var(--color-border);
  margin-bottom: var(--space-normal);

  .webhook-actions {
    display: flex; 
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
  }
}


.filter-actions {
  margin-top: var(--space-normal);
}

.event_wrapper {
  select {
    margin: var(--space-zero);
  }

  .info-message {
    font-size: var(--font-size-mini);
    color: var(--s-500);
    text-align: right;
  }

  margin-bottom: var(--space-medium);
}
</style>